<template>
  <main class="employee-payroll">
    <h1>Расчет заработной платы</h1>
    <strong class="employee-payroll__description">
      Перейти к
      <router-link v-bind:to="{ name: 'ClinicPanelReportEmployeesPayroll' }">
        отчету по расчетам
      </router-link>
    </strong>
    <form
      class="employee-payroll__filters filters"
      v-if="hasFetchedOptions"
      v-on:submit.prevent
    >
      <date-range-with-presets
        v-bind:presets="presetsOfDates"
        v-bind:value="selectedDates"
        custom-color-date="black"
        v-on:input="onInputDates" />
      <multiple-autocomplete-input
        v-bind:disabled="isAwaitRequest"
        label="Выберите сотрудников"
        removal-per-chip
        select-all-text="Все сотрудники"
        set-select-all-option
        v-bind:items="employeeOptions"
        v-model="selectedEmployees" />
      <the-select
        class="filters__select-type-calculation"
        title="Тип расчета"
        v-bind:value="selectedCalculatedType"
        v-bind:options="calculatedTypes"
        v-on:input="onInputCalculatedType"
      />
    </form>
    <div class="employee-payroll__scroll">
      <table class="employee-payroll-table employee-payroll__table">
        <thead class="employee-payroll-table__head-line">
          <tr>
            <th
              class="employee-payroll-table__head-cell"
              v-bind:key="title"
              v-for="title in payrollColumns"
            >{{ title }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="employee-payroll-table__body-line"
            v-bind:key="lineKey"
            v-for="{ cells, lineKey } in bodyLines"
          >
            <td
              class="employee-payroll-table__body-cell"
              v-bind:key="cellKey"
              v-for="{
                cellKey,
                links,
                title,
                linkTo,
              } in cells"
            >
              <span v-if="title && !linkTo">{{ title }}</span>
              <router-link
                class="employee-payroll-table__name-link"
                title="Открыть"
                v-if="title && linkTo"
                v-bind:to="linkTo.to"
              >
                <span>{{ title }}</span>
              </router-link>
              <router-link
                class="employee-payroll-table__link"
                v-bind:class="tagClass"
                v-bind:key="linkKey"
                v-bind:title="title"
                v-bind:to="to"
                v-for="{
                  linkKey,
                  tagClass,
                  title,
                  to,
                } in links" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <form
      class="employee-payroll__actions"
      v-if="calculationId"
      v-on:submit.prevent
    >
      <button
        class="employee-payroll__accrue"
        type="button"
        v-bind:disabled="accrualDisabled"
        v-on:click="onAccruePayroll"
      >Начислить</button>
      <button
        class="employee-payroll__excel"
        type="button"
        v-on:click="exportToExcel"
      >Выгрузить в Excel</button>
    </form>
  </main>
</template>
<script>
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import { getISODateStringByDate } from '@/mixins/dateMixin';
import notifierMixin from '@/mixins/notifierMixin';
import { SELECTED_CALCULATED_TYPE, SELECTED_EMPLOYEES } from '@/store/actions/payroll';
import TheSelect from '@/components/TheSelect.vue';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import debounce from 'lodash.debounce';

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

/**
 * @description Дни расчета для по возвращению в раздел.
 * @type {(Date[]|null)}
 */
let restorableSelectedDates = null;

/**
 * @description Раздел начисления заработной платы сотрудников.
 * @type {Vue}
 */
export default {
  beforeRouteEnter(_, __, next) {
    if (restorableSelectedDates !== null) {
      next((vm) => { vm.replaceSelectedDates(restorableSelectedDates); });
    } else {
      next();
    }
  },
  beforeRouteLeave(to, _, next) {
    const needsRestoredSelectedPeriod = this.hasNonDefaultSelectedPeriod && (
      to.name === 'ClinicPanelEmployeePayrollSchema'
      || to.name === 'ClinicPanelEmployeePayrollDetails'
      || to.name === 'ClinicPanelReportEmployeesPayroll'
    );
    restorableSelectedDates = needsRestoredSelectedPeriod ? this.selectedDates : null;
    next();
  },
  components: {
    TheSelect,
    DateRangeWithPresets,
    MultipleAutocompleteInput,
  },
  computed: {
    accrueDialogTitle() {
      return this.selectedCalculatedType === 'by_service' ? 'ПО СЧЕТАМ' : 'ПО ОПЛАТАМ';
    },
    /**
     * @description Начисление отключено.
     * @type {boolean}
     */
    accrualDisabled() {
      return this.calculationId === this.lastAccruedCalculationId
        || this.allowedActions.includes('accrual') !== true;
    },
    /**
     * @description Ряды в теле расчета.
     * @type {string[][]}
     */
    bodyLines() {
      return this.payrollRows.map((row, lineIndex) => {
        const lineKey = `employee-rows:${lineIndex}`;
        const cells = [
          ...row.fields.map((title, cellIndex) => ({
            cellKey: `${lineKey}:cells:${cellIndex}`,
            links: [],
            title,
            linkTo: this.makeLinkToDetailsByName(cellIndex, row.links[0].identity),
          })),
          this.makeCellWithLinks(lineKey, row)('employee-schemas'),
        ];
        return {
          cells,
          lineKey,
        };
      });
    },
    /**
     * @description Набор предварительных дат для фильтрации.
     * @type {object[]}
     */
    presetsOfDates() {
      return this
        .periodOptions
        .map(({ from_to: [from, to], title, value }) => {
          const periodDates = [new Date(from), new Date(to)];
          return {
            id: value,
            label: title,
            title,
            toValue: () => periodDates,
          };
        });
    },
    selectedEmployees: {
      get() {
        return this.$store.getters.getSelectedEmployees;
      },
      set(newValue) {
        if (!this.isAwaitRequest) {
          this.$store.commit(SELECTED_EMPLOYEES, newValue ?? []);
        }
      },
    },
    selectedCalculatedType: {
      get() {
        return this.$store.getters.getSelectedCalculatedType;
      },
      set(newValue) {
        if (!this.isAwaitRequest) {
          this.$store.commit(SELECTED_CALCULATED_TYPE, newValue ?? '');
        }
      },
    },
  },
  created() {
    this
      .fetchPayrollOptions()
      .then(() => {
        this
          .calculatedTypes
          .slice(1, 2) // by_service
          .forEach(({ value }) => {
            this.selectedCalculatedType = this.selectedCalculatedType || value;
          });
        this
          .presetsOfDates
          .slice(2, 3) // month
          .forEach(({ toValue }) => {
            const dates = toValue();
            this.defaultSelectedDates = dates;
            if (this.hasNonDefaultSelectedPeriod !== true) {
              this.selectedDates = dates;
            }
          });
        this.fetchCalculatedPayroll();
      })
      .finally(() => { this.$nextTick(() => { this.hasFetchedOptions = true; }); });
  },
  data() {
    return {
      allowedActions: [],
      calculationId: null,
      defaultSelectedDates: [],
      employeeOptions: [],
      hasFetchedOptions: false,
      hasNonDefaultSelectedPeriod: false,
      lastAccruedCalculationId: null,
      payrollColumns: [],
      payrollRows: [],
      periodOptions: [],
      selectedDates: [],
      calculatedTypes: [],
      isAwaitRequest: false,
    };
  },
  methods: {
    /**
     * @description Выгрузить расчет в Excel таблицу.
     * @returns {void}
     */
    exportToExcel() {
      this
        .$payrollGateway
        .downloadEmployeesPayrollExcelSpreadsheet(this.calculationId)
        .then((result) => {
          result.forEachLeft((data) => { this.notifyFail(data.title); });
          result.forEach((file) => { saveFile(file, 'Расчет заработной платы.xls'); });
        });
    },
    /**
     * @description Получить расчет заработной платы.
     * @returns {void}
     */
    fetchCalculatedPayroll() {
      this.isAwaitRequest = true;
      const [dateFrom, dateTo] = this.selectedDates.map(getISODateStringByDate);
      this
        .$payrollGateway
        .formPayroll(this.selectedEmployees, dateFrom, dateTo, this.selectedCalculatedType)
        .then((result) => {
          result.forEach(({
            actions,
            calculation_id: calculationId,
            columns,
            rows,
          }) => {
            this.allowedActions = actions;
            this.calculationId = calculationId;
            this.payrollColumns = [...columns, 'Схема расчета ЗП'];
            this.payrollRows = rows;
          });
        })
        .finally(() => {
          this.isAwaitRequest = false;
        });
    },
    /**
     * @description Получить опции расчета.
     * @returns {Promise}
     */
    fetchPayrollOptions() {
      return this.$payrollGateway.viewOptions().then((result) => {
        result.forEach(({ employees, periods, calculated_types: calculatedTypes }) => {
          this.employeeOptions = employees;
          this.periodOptions = periods;
          this.calculatedTypes = calculatedTypes;
        });
      });
    },
    /**
     * @description Получить отображения для ячейки ссылки в строке.
     * @param {string} lineKey
     * @param {array}  row
     * @returns {function}
     */
    makeCellWithLinks(lineKey, row) {
      return (linkIdentity) => {
        const links = row.links.flatMap(({ identity, to }) => {
          if (linkIdentity !== to) {
            return [];
          }
          const linkKey = `${lineKey}:links:${to}`;
          const allowedLinks = {
            'employee-schemas': {
              linkKey,
              tagClass: ['schema'],
              title: 'Перейти к настройке схемы расчета',
              to: {
                name: 'ClinicPanelEmployeePayrollSchema',
                params: { id: identity },
              },
            },
            'payslip-details': {
              linkKey,
              tagClass: ['details'],
              title: 'Перейти к детализации расчета',
              to: {
                name: 'ClinicPanelEmployeePayrollDetails',
                params: {
                  calculationId: this.calculationId,
                  employeeId: identity,
                },
              },
            },
          };
          return [allowedLinks[linkIdentity]].filter(Boolean);
        });
        return {
          cellKey: `${lineKey}:cells:${linkIdentity}-link`,
          links,
          title: '',
          linkTo: '',
        };
      };
    },
    /**
     * @description Создать ссылку перехода к детализации на имени.
     * @param {string} cellIndex
     * @param {string} employeeId
     */
    makeLinkToDetailsByName(cellIndex, employeeId) {
      if (cellIndex === 0) {
        return {
          title: 'Перейти к детализации расчета',
          to: {
            name: 'ClinicPanelEmployeePayrollDetails',
            params: {
              calculationId: this.calculationId,
              employeeId,
            },
          },
        };
      }
      return '';
    },
    async onAccruePayroll() {
      const dialog = await this.showAccrueDialog(`Начисление заработной платы будет осуществлено: ${this.accrueDialogTitle}`);
      if (dialog.result !== DialogResult.ACCRUE) {
        return;
      }
      const { calculationId } = this;
      this
        .$payrollGateway
        .accrueCalculation(calculationId)
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.lastAccruedCalculationId = calculationId;
          });
        });
    },
    /**
     * Обработать выбор типа расчета.
     * @return {void}
     */
    onInputCalculatedType(value) {
      this.selectedCalculatedType = value;
      this.fetchCalculatedPayroll();
    },
    /**
     * @description Обработать выбор периода для расчета.
     * @returns {void}
     */
    onInputDates(value) {
      if (value.length === 2) {
        this.selectedDates = value.sort(
          (first, second) => Number(first.getTime() > second.getTime()),
        );
        this.fetchCalculatedPayroll();
        this.hasNonDefaultSelectedPeriod = [0, 1].some(
          (index) => this.selectedDates[index] !== this.defaultSelectedDates[index],
        );
      } else {
        this.selectedDates = value;
      }
    },
    /**
     * @description Заменить выбранные даты.
     * @param {Date[]} value
     * @returns {void}
     */
    replaceSelectedDates(value) {
      this.hasNonDefaultSelectedPeriod = true;
      this.selectedDates = value;
    },
  },
  mixins: [
    notifierMixin,
    builtInDialogMixin,
  ],
  name: 'ClinicPanelEmployeePayroll',
  watch: {
    selectedEmployees: {
      handler: debounce(function _(value) {
        if (!this.isAwaitRequest) {
          this.selectedEmployees = value;
          this.fetchCalculatedPayroll();
        }
      }, 1000),
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";

.employee-payroll {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow: scroll;
  padding: 2rem 1rem;
  &__description {
    a:hover {
      text-decoration: none;
    }
  }
  &__accrue {
    @include buttons.btn-blue(13rem, 3rem, 1rem);
    &:disabled {
      background-color: var(--unfocus-color);
    }
  }
  &__actions {
    display: flex;
    flex-direction: row;
    gap: 1rem;
  }
  &__excel {
    @include buttons.btn-transparent(13rem, 3rem, 1rem);
  }
  &__filters {
    align-items: baseline;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
  }
  &__table {
    width: 100%;
  }
  &-table {
    border-collapse: collapse;
    font-size: .875rem;
    line-height: 1rem;
    overflow: auto;
    table-layout: fixed;
    tr {
      display: grid;
      grid-template-columns: 1fr 200px 150px 150px 150px 200px 150px;
    }
    &__body {
      &-cell {
        padding: 15px 8px;
        text-align: left;
        text-transform: none;
        white-space: nowrap;
      }
      &-line {
        border-bottom: 1px solid var(--fill-color);
        &:hover {
          background: #eaeff3;
        }
      }
    }
    &__head {
      &-cell {
        padding: 10px 8px;
        text-align: left;
      }
      &-line {
        border-bottom: 2px solid var(--action-color);
        font-weight: 700;
      }
    }
    &__name-link {
      text-decoration: none;
      color: black !important;
      transition: color var(--animate-duration);
      &:hover {
        cursor: pointer;
        color: var(--action-color) !important;
      }
    }
    &__link {
      &:after {
        color: var(--action-color);
        display: inline-block;
        font-size: 18px;
        font-weight: 400;
        text-align: center;
        text-decoration: none;
        width: 30px;
      }
      &.details::after {
        content: '\1f441';
      }
      &.schema::after {
        content: '\20b8';
      }
    }
  }
}
.filters {
  &__select-type-calculation {
    align-self: flex-start;
    width: 100%;
    max-width: 270px;
  }
}
@media screen and (max-width: 1330px) {
  .employee-payroll {
    &__scroll {
      overflow: scroll;
    }
    &-table {
      width: 1120px;
      tr {
        grid-template-columns: 200px 180px 120px 120px 150px 190px 140px;
      }
    }
  }
}
</style>
